"use client"

import * as React from "react"
import Link from "next/link"
import { SafeImage } from "@/components/ui/safe-image"
import { accentColorFor } from "./types"
import type { IndexBrand } from "./types"
import { isWideWordmark } from "./logo-shape"

/** Initials fallback for partner tiles without a logo_path. Mirrors
 *  `deriveInitials` in `brand-logo.tsx` so both surfaces look consistent. */
function deriveInitials(name: string): string {
  const cleaned = (name || "").replace(/\(.*?\)/g, "").trim()
  const words = cleaned.split(/[\s&+\-']/).filter(Boolean)
  if (words.length === 0) return "?"
  if (words.length === 1) return words[0].charAt(0).toUpperCase()
  return (words[0].charAt(0) + (words[1] || "").charAt(0)).toUpperCase()
}

interface FeaturedLogoBarProps {
  /** Curated partner brands ordered by `display_order` (Samsung, LG, GE…). */
  partners: IndexBrand[]
}

/**
 * Editor's Pick · Top Refrigerator Brands.
 *
 * Six full brand cards + one orange "+N more" card, laid out as a 7-column
 * grid at md+ so each cell splits the container width evenly. Each card is
 * a structured tile: small rounded logo box + brand name + "{N} models"
 * sub-label below. Matches the editor's-pick band in the handoff design.
 *
 * FRIDGE-1495 (10/17/2026): legible wordmarks. On production the six-across
 * row from 768 px drew KitchenAid 75 x 7.3 at 768 and 109 x 10.7 at 1024; a
 * 10:1 mark needs a 110 px box to paint 11 px tall, which six tiles across a
 * 704 px row cannot give. The bar is three across below 1280 px and six from
 * 1280 px (KitchenAid 173 px wide there). A wide wordmark (logo-shape.ts) also
 * fills its tile, under contain with the card tile's 6 px padding: scale-down
 * drew it at the size `sizes` implies for the file, which for a 300 px file at
 * 430 px was 90 x 9. Two across a phone, a 10:1 mark cannot reach 14 px tall
 * below about 360 px: the tile is 130 px wide at 320 px (KitchenAid 116 x 11.6).
 */
export function FeaturedLogoBar({ partners }: FeaturedLogoBarProps) {
  if (partners.length === 0) return null
  const visible = partners.slice(0, 6)

  return (
    <section className="mt-2 mb-8" aria-labelledby="featured-brands-heading">
      <div className="mb-5 flex items-center justify-center gap-2">
        <span className="h-px w-10 bg-[#ff9951]/30" aria-hidden />
        <h2
          id="featured-brands-heading"
          className="text-[11.5px] font-extrabold uppercase tracking-[0.16em] text-[#ff9951]"
        >
          Editor&rsquo;s Pick · Top Refrigerator Brands
        </h2>
        <span className="h-px w-10 bg-[#ff9951]/30" aria-hidden />
      </div>

      {/* Tiles mirror the `MoreBrandsStrip` design on `/brands/[slug]` —
          fixed-height (112px) rounded-[12px] card, generous logo cap
          (max-h-[78px] / max-w-[92%]), 3px hover lift with a strong shadow.
          Keeping the two surfaces visually identical reinforces that this
          band and the related-brands band are the same component family. */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
        {visible.map((b, index) => {
          const hasReal = !!b.logo_path
          const wideMark = hasReal && isWideWordmark(b.logoAspect)
          const accent = accentColorFor(b.slug)
          return (
            <Link
              key={b.slug}
              href={`/brands/${b.slug}`}
              prefetch={false}
              title={b.name}
              aria-label={b.name}
              className={`group grid h-[112px] place-items-center rounded-[12px] border border-gray-200 bg-white ${wideMark ? "p-1.5" : "p-3"} transition-all duration-200 hover:-translate-y-[3px] hover:border-[#ff9951] hover:shadow-[0_14px_28px_-16px_rgba(0,0,0,0.18)]`}
            >
              {wideMark ? (
                <SafeImage
                  src={b.logo_path!}
                  alt={`${b.name} logo`}
                  width={300}
                  height={156}
                  sizes="(max-width: 640px) 45vw, (max-width: 1279px) 30vw, 16vw"
                  loading={index < 4 ? 'eager' : undefined}
                  // FRIDGE-1495: the tile's inner box, inline (FRIDGE-1540), under contain, so the
                  // mark paints to the box's width whatever size the served file claims.
                  style={{ width: '100%', height: 78, objectFit: 'contain' }}
                />
              ) : hasReal ? (
                <SafeImage
                  src={b.logo_path!}
                  alt={`${b.name} logo`}
                  width={300}
                  height={156}
                  sizes="(max-width: 640px) 45vw, (max-width: 1279px) 30vw, 16vw"
                  // FRIDGE-1506: the first two rows on a phone sit in the first screen.
                  loading={index < 4 ? 'eager' : undefined}
                  // FRIDGE-1540: the old caps are the box, so the lazy logos have a size before
                  // they load. Their drawn size comes from `sizes`, so a file can fit the box at
                  // one width and not at another; scale-down draws it at that size when it fits
                  // and shrinks it when it does not, centered, exactly as the caps did.
                  style={{ width: '92%', height: 78, objectFit: 'scale-down' }}
                />
              ) : (
                <div className="grid h-[72px] w-[72px] place-items-center rounded-[10px] bg-gray-50">
                  <span
                    aria-hidden
                    className="text-[28px] font-black leading-none tracking-[-0.02em]"
                    style={{ color: accent }}
                  >
                    {deriveInitials(b.name)}
                  </span>
                </div>
              )}
            </Link>
          )
        })}
      </div>
    </section>
  )
}
